import type { EquipmentTypes, IProfession, UtilityTypes } from '@d-orca/shared/trusstor';
import type { IMonitorReplay, IReplayEvent, IReplayFloorFrame, IReplayFrame } from '@d-orca/shared/client/interfaces';
import {
  aggregateTradeChips,
  OTHERS_TRADE_GROUP,
  type EquipmentPresence,
  type FloorWithChips,
  type ManagerPresence,
  type UtilityPresence,
} from '@store/selectors/monitor.selectors';
import type { IReplayRail } from './replayRail';

/** The day's equipment dictionary: name and type per equipment id. */
export type ReplayEquipment = IMonitorReplay['equipment'];

/** The building and Live Summary at the playhead, in the live selectors' shapes. */
export interface IReplayView {
  chipsByFloor: Map<string, FloorWithChips>;
  managersByFloor: Map<string, ManagerPresence[]>;
  equipmentByFloor: Map<string, EquipmentPresence[]>;
  utilitiesByFloor: Map<string, UtilityPresence[]>;
  /** null before the day's first frame: no data, never "0 workers". */
  summary: { workersOnSite: number; activeFloors: number; managers: number; activeTrades: number } | null;
  /** The frame's `t` (drives the chip glide); null before the first frame. */
  frameKey: string | null;
  /** The day's equipment dictionary (names for the event toasts). */
  equipment: ReplayEquipment;
  activeEvents: readonly IReplayEvent[];
  /** The LeftRail lists and safety alerts at the playhead. */
  rail: IReplayRail;
}

/** The LeftRail filters in the replay's terms; null = no filter, as an empty live selection. */
export interface IReplayFilters {
  professionIds: ReadonlySet<string> | null;
  /** Frames carry manager tag ids and names, the filter manager ids: matched by name. */
  managerNames: ReadonlySet<string> | null;
  equipmentTypes: ReadonlySet<string> | null;
}

const whitelist = (ids: readonly string[]) => (ids.length > 0 ? new Set(ids) : null);

/**
 * The live selections (the same selectors live reads) as replay filters. Manager ids
 * resolve through the site's whole manager list (`selectManagers`, disconnected
 * included — as the URL sync resolves them), never the on-site rows, so a manager
 * absent now still filters a past minute.
 */
export function replayFilters(
  professionIds: readonly string[],
  managerIds: readonly string[],
  managers: readonly { _id?: string; name?: string }[],
  equipmentTypes: readonly string[],
): IReplayFilters {
  const selected = whitelist(managerIds);
  const names = (ids: ReadonlySet<string>) =>
    new Set(managers.flatMap((m) => (m.name && ids.has(m._id ?? m.name) ? [m.name] : [])));
  return {
    professionIds: whitelist(professionIds),
    managerNames: selected && names(selected),
    equipmentTypes: whitelist(equipmentTypes),
  };
}

/** Keeps `items` whose key is in `allowed`; everything when there is no filter. */
const allowedBy = <T>(items: T[], allowed: ReadonlySet<string> | null, key: (item: T) => string) =>
  allowed ? items.filter((item) => allowed.has(key(item))) : items;

/** A manager seen in replay who is not on site now has no live colour. */
const NEUTRAL_MANAGER_COLOR = 'var(--g500)';

/** One chip per trade by the live chip rule, from the frame's per-profession counts. */
const tradeChips = (counts: Record<string, number>, profById: Map<string, IProfession>, allowed: IReplayFilters['professionIds']) =>
  aggregateTradeChips(
    allowedBy(Object.entries(counts), allowed, ([id]) => id).map(([_id, count]) => ({ _id, count })),
    profById,
  );

/** Live drops a manager with no name (`toManagerPresence`); replay does the same. */
export const namedManagers = (floor: IReplayFloorFrame) => floor.managers.filter((manager) => manager.name.length > 0);

function equipmentByType(
  ids: string[],
  equipment: ReplayEquipment,
  allowed: IReplayFilters['equipmentTypes'],
): EquipmentPresence[] {
  const counts = new Map<EquipmentTypes, number>();
  for (const id of ids) {
    const type = equipment[id]?.type as EquipmentTypes | undefined;
    if (type && (!allowed || allowed.has(type))) counts.set(type, (counts.get(type) ?? 0) + 1);
  }
  return [...counts].map(([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count);
}

const sum = (counts: Record<string, number>) => Object.values(counts).reduce((total, n) => total + n, 0);

function summarise(frame: IReplayFrame, profById: Map<string, IProfession>): NonNullable<IReplayView['summary']> {
  const floors = Object.values(frame.floors);
  const trades = new Set<string>();
  const managers = new Set<string>();
  for (const floor of floors) {
    namedManagers(floor).forEach((m) => managers.add(m.tagId));
    for (const [professionId, count] of Object.entries(floor.professions)) {
      const prof = profById.get(professionId);
      if (count > 0 && prof?.tradeId && prof.tradeGroup !== OTHERS_TRADE_GROUP) trades.add(prof.tradeId);
    }
  }
  return {
    workersOnSite: frame.workers,
    activeFloors: floors.filter((floor) => sum(floor.professions) > 0).length,
    managers: managers.size,
    activeTrades: trades.size,
  };
}

/** Adds `value` under `key` only when the list is non-empty, as the live per-floor maps do. */
function setNonEmpty<T>(map: Map<string, T[]>, key: string, value: T[]): void {
  if (value.length > 0) map.set(key, value);
}

/** A frame floor the live list doesn't carry (e.g. the live stream hasn't delivered): labelled by its id. */
const unknownFloor = (floorId: string): FloorWithChips => ({
  floorId,
  label: floorId,
  workersCount: 0,
  alertsCount: 0,
  isSpecial: false,
  isHoist: false,
  isMovingFloor: false,
  chips: [],
});

/**
 * Folds one replay frame into the live selectors' shapes so the building and the
 * Live Summary render it unchanged. The frame's floors drive the maps — replay never
 * depends on live presence; the live floors only lend a floor's label and flags
 * (the frame also holds the special areas, which the building simply doesn't anchor). `frame = null`
 * (before the first frame) yields empty maps and a null summary. The LeftRail filters narrow the
 * chips, managers and equipment as live's selectors do; like live, the worker
 * counts and the Live Summary stay unfiltered (`selectLiveSummary` ignores them).
 */
export function buildReplayView(
  frame: IReplayFrame | null,
  liveFloors: FloorWithChips[],
  professions: IProfession[],
  equipment: ReplayEquipment,
  liveManagerColors: ReadonlyMap<string, string>,
  filters: IReplayFilters,
): Omit<IReplayView, 'activeEvents' | 'rail'> {
  const view: Omit<IReplayView, 'activeEvents' | 'rail'> = {
    chipsByFloor: new Map(),
    managersByFloor: new Map(),
    equipmentByFloor: new Map(),
    utilitiesByFloor: new Map(),
    summary: null,
    frameKey: null,
    equipment,
  };
  if (!frame) return view;
  const profById = new Map(professions.map((p) => [p._id, p]));
  const liveById = new Map(liveFloors.map((f) => [f.floorId, f]));
  for (const [floorId, floor] of Object.entries(frame.floors)) {
    view.chipsByFloor.set(floorId, {
      ...(liveById.get(floorId) ?? unknownFloor(floorId)),
      workersCount: sum(floor.professions),
      chips: tradeChips(floor.professions, profById, filters.professionIds),
    });
    const managers = allowedBy(namedManagers(floor), filters.managerNames, (m) => m.name).map((m) => ({
      id: m.tagId,
      name: m.name,
      backgroundColor: liveManagerColors.get(m.tagId) ?? NEUTRAL_MANAGER_COLOR,
    }));
    setNonEmpty(view.managersByFloor, floorId, managers.sort((a, b) => a.name.localeCompare(b.name)));
    setNonEmpty(view.equipmentByFloor, floorId, equipmentByType(floor.equipmentIds, equipment, filters.equipmentTypes));
    setNonEmpty(
      view.utilitiesByFloor,
      floorId,
      floor.utilities.map((u) => ({ id: u.tagId, type: u.utilityType as UtilityTypes, name: '', isLive: false })),
    );
  }
  return { ...view, summary: summarise(frame, profById), frameKey: frame.t };
}
